export type PlanRate = {
  rate: number;
  tilgung: number;
  zinsen: number;
  annuitaet: number;
  restschuld: number;
};

const calcAnnuitaetenTilgerPlan = async (
  data: AllTilgungsData,
  options: TilgungOptions
): Promise<PlanRate[]> => {
  const plan: PlanRate[] = [];

  // Hier wird die Zinsbindungsfrist als Laufzeit für den anzuzeigenden Tilgungsplan benutzt!
  // Anzahl anzuzeigende Raten berechnen aus Perioden und Laufzeit (oder Zinsbinden, immer bis zum kleineren Wert)
  const raten =
    data.zinsbindung < data.laufzeit
      ? data.zinsbindung * options.ratesPerYear
      : data.laufzeit * options.ratesPerYear;
  const tilgungsFreiePerioden = data.tilgunsfreieJahre * options.ratesPerYear;

  // Berechnung Annuität mit vorgegebener Zins- und Tilgungssatz
  const annuitaet =
    (data.darlBetrag * (data.zinssatzDecimal + data.tilgungssatzDezimal)) /
    options.ratesPerYear;

  let start = 0;

  // tilgungsfreie Anlaufjahre berücksichtigen und Zinsen berechnen
  // Zinsen berechnen - Tilgungsfreie Raten berechnen
  for (; start < tilgungsFreiePerioden; start++) {
    const zinsen = roundNumber(
      (data.darlBetrag * data.zinssatzDecimal) / options.ratesPerYear,
      options.accuracy
    );
    plan[start] = {
      rate: start + 1,
      zinsen: zinsen,
      annuitaet: zinsen,
      tilgung: 0,
      restschuld: data.darlBetrag
    };
  }

  // Rate berechnen
  for (; start < raten; start++) {
    const lastSchuld = plan[start - 1]
      ? plan[start - 1].restschuld
      : data.darlBetrag;
    const zinsen = roundNumber(
      (lastSchuld * data.zinssatzDecimal) / options.ratesPerYear,
      options.accuracy
    );
    const tilgung = annuitaet - zinsen;
    plan[start] = {
      rate: start + 1,
      zinsen: zinsen,
      annuitaet: annuitaet,
      tilgung: tilgung,
      restschuld: lastSchuld - tilgung
    };
  }
  return plan;
};

export type TilgungFormData = {
  program: string;
  darlBetrag: number;
  zinsbindung: string;
  zinssatz: string;
  laufzeit: string;
  tilgunsfreieJahre: string;
};
type TilgungParsedFormData = {
  program: string;
  darlBetrag: number;
  zinsbindung: number;
  zinssatz: number;
  laufzeit: number;
  tilgunsfreieJahre: number;
};
type TilgungsData = TilgungParsedFormData & {
  annuitaet?: number;
  zinssatzDecimal?: number;
  tilgungssatz?: number;
  tilgungssatzDezimal?: number;
};
type AllTilgungsData = {
  [P in keyof TilgungsData]: TilgungsData[P];
};
type TilgungOptions = {
  ratesPerYear?: number;
  accuracy?: number;
};
export type TilgungsReturnData = {
  overview: TilgungsData;
  plan: PlanRate[];
};

const roundNumber = (num: number, accuracy: number): number => {
  const d = Math.pow(10, accuracy);
  return Number((Math.round(num * d) / d).toFixed(accuracy));
};

// Tilgungssatz berechnen
const calcTilgungssatz = (data: TilgungsData, options: TilgungOptions) =>
  roundNumber(
    ((data.annuitaet * options.ratesPerYear) / data.darlBetrag -
      data.zinssatzDecimal) *
      100,
    options.accuracy
  );

// Annuität berechnen
const calcAnnuitat = (data: TilgungsData, options: TilgungOptions) =>
  roundNumber(
    (data.darlBetrag *
      Math.pow(
        1 + data.zinssatzDecimal / options.ratesPerYear,
        (data.laufzeit - data.tilgunsfreieJahre) * options.ratesPerYear
      ) *
      (data.zinssatzDecimal / options.ratesPerYear)) /
      (Math.pow(
        1 + data.zinssatzDecimal / options.ratesPerYear,
        (data.laufzeit - data.tilgunsfreieJahre) * options.ratesPerYear
      ) -
        1),
    options.accuracy
  );

export const calcTilgungsPlan = async (
  data: TilgungParsedFormData,
  options: TilgungOptions = {
    ratesPerYear: 12,
    accuracy: 3
  }
): Promise<TilgungsReturnData> => {
  const tilgungsdata: TilgungsData = {
    ...data,
    zinssatzDecimal: data.zinssatz / 100
  };
  // Annuität und anfänglichen Tilgungssatz berechnen
  tilgungsdata.annuitaet = calcAnnuitat(tilgungsdata, options);
  tilgungsdata.tilgungssatz = calcTilgungssatz(tilgungsdata, options);
  tilgungsdata.tilgungssatzDezimal = tilgungsdata.tilgungssatz / 100;
  
  return {
    overview: tilgungsdata,
    plan: await calcAnnuitaetenTilgerPlan(tilgungsdata, options)
  };
};
export default calcTilgungsPlan;
